import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../components/Layout.jsx";
import axiosInstance from "../services/axiosInstance";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import "./Client.css";

const TAView = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [submission, setSubmission] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationType, setNotificationType] = useState("");
  const [notificationMessage, setNotificationMessage] = useState("");

  useEffect(() => {
    fetchSubmissionDetails();
  }, [id]);

  const fetchSubmissionDetails = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/api/ta/submissions/${id}`);
      if (response.data.success) {
        setSubmission(response.data.data.submission);
        setItems(response.data.data.items);
      }
    } catch (err) {
      setError("Failed to fetch submission details");
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, message) => {
    setNotificationType(type);
    setNotificationMessage(message);
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 3000);
  };

  const downloadPDF = () => {
    if (!submission || !items.length) return;

    const doc = new jsPDF();
    doc.setFontSize(12);
    doc.text("Build INT", 20, 15);
    doc.setFontSize(10);
    doc.text("Travel Allowance Form", 20, 22);
    doc.setDrawColor(200);
    doc.line(20, 25, 190, 25);

    let yPosition = 35;

    // Employee Details
    doc.text(`Employee: ${submission.employee_name}`, 20, yPosition);
    yPosition += 6;
    doc.text(`Month/Year: ${submission.month_year}`, 20, yPosition);
    yPosition += 6;
    doc.text(`Designation: ${submission.designation}`, 20, yPosition);
    yPosition += 6;
    doc.text(`Location: ${submission.location || "N/A"}`, 20, yPosition);
    yPosition += 10;

    // Items Table
    const tableData = items.map((item) => [
      item.item_date,
      item.site,
      item.particulars,
      item.mode,
      `₹${parseFloat(item.amount).toFixed(2)}`,
    ]);

    autoTable(doc, {
      head: [["Date", "Site", "Particulars", "Mode", "Amount"]],
      body: tableData,
      startY: yPosition,
      headStyles: { fillColor: [41, 128, 185], textColor: [255, 255, 255] },
      bodyStyles: { fillColor: [245, 245, 245] },
    });

    const finalY = doc.lastAutoTable.finalY + 10;
    const totalAmount = items.reduce((sum, item) => sum + parseFloat(item.amount), 0);
    doc.text(`Total Amount: ₹${totalAmount.toFixed(2)}`, 20, finalY);
    doc.text(`Status: ${submission.status}`, 20, finalY + 6);

    doc.save(`TA_${submission.id}.pdf`);
    showMessage("success", "PDF downloaded successfully");
  };

  const downloadExcel = () => {
    showMessage("info", "Excel download feature coming soon");
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { bg: "warning", text: "Pending Approval" },
      approved: { bg: "success", text: "Approved" },
      rejected: { bg: "danger", text: "Rejected" },
    };
    const style = statusMap[status] || statusMap.pending;
    return (
      <span className={`badge bg-${style.bg}`} style={{ fontSize: "0.9rem" }}>
        {style.text}
      </span>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="alert alert-danger">
          <i className="bi bi-exclamation-circle me-2"></i>
          {error}
        </div>
      </Layout>
    );
  }

  if (!submission) {
    return (
      <Layout>
        <div className="alert alert-info">
          <i className="bi bi-info-circle me-2"></i>
          Submission not found.
        </div>
      </Layout>
    );
  }

  const totalAmount = items.reduce((sum, item) => sum + parseFloat(item.amount), 0);

  return (
    <Layout>
      {/* Notification */}
      {showNotification && (
        <div
          style={{
            position: "fixed",
            top: "20px",
            right: "20px",
            zIndex: 9999,
            padding: "12px 16px",
            borderRadius: "6px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            minWidth: "280px",
            backgroundColor:
              notificationType === "success" ? "#d4edda" : "#d1ecf1",
            color: notificationType === "success" ? "#155724" : "#0c5460",
            border: `1px solid ${
              notificationType === "success" ? "#c3e6cb" : "#bee5eb"
            }`,
          }}
        >
          <i
            className={`bi ${
              notificationType === "success"
                ? "bi-check-circle-fill"
                : "bi-info-circle-fill"
            }`}
            style={{ fontSize: "1.2rem" }}
          ></i>
          <span style={{ flex: 1 }}>{notificationMessage}</span>
        </div>
      )}

      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div className="d-flex align-items-center">
          <button
            onClick={() => navigate("/ta-list")}
            className="btn btn-link text-dark p-0 me-2"
            style={{ fontSize: "1.8rem", lineHeight: 1, textDecoration: "none" }}
            title="Go back"
          >
            <i className="bi bi-arrow-left"></i>
          </button>
          <h2 style={{ margin: 0, fontSize: "1.8rem", fontWeight: 500, color: "var(--secondary)" }}>
            TA Submission Details
          </h2>
        </div>
        <div className="btn-group btn-group-sm" role="group">
          <button onClick={downloadPDF} className="btn btn-outline-info" title="Download PDF" aria-label="Download PDF">
            <i className="bi bi-file-pdf"></i>
          </button>
          <button onClick={downloadExcel} className="btn btn-outline-success" title="Download Excel" aria-label="Download Excel">
            <i className="bi bi-file-excel"></i>
          </button>
        </div>
      </div>

      {/* Details Card */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row mb-3">
            <div className="col-md-6">
              <p>
                <strong>Employee Name:</strong> {submission.employee_name}
              </p>
              <p>
                <strong>Month/Year:</strong> {submission.month_year}
              </p>
              <p>
                <strong>Designation:</strong> {submission.designation}
              </p>
            </div>
            <div className="col-md-6">
              <p>
                <strong>Cost Center:</strong> {submission.cost_center || "N/A"}
              </p>
              <p>
                <strong>Location:</strong> {submission.location || "N/A"}
              </p>
              <p>
                <strong>Status:</strong> {getStatusBadge(submission.status)}
              </p>
            </div>
          </div>
          <div className="row">
            <div className="col-md-6">
              <p>
                <strong>Supporting TA:</strong> {submission.supporting_ta || "N/A"}
              </p>
            </div>
            <div className="col-md-6">
              <p>
                <strong>Payment Method:</strong> {submission.payment || "N/A"}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Items Table */}
      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">Expense Items ({items.length})</h5>
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th>Date</th>
                  <th>Site</th>
                  <th>Particulars</th>
                  <th>Mode</th>
                  <th className="text-end">Amount</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td>{item.item_date}</td>
                    <td>{item.site}</td>
                    <td>{item.particulars}</td>
                    <td>
                      <span className="badge bg-light text-dark">{item.mode}</span>
                    </td>
                    <td className="text-end">
                      <strong>₹{parseFloat(item.amount).toFixed(2)}</strong>
                    </td>
                  </tr>
                ))}
                <tr className="table-active">
                  <td colSpan="4" className="text-end">
                    <strong>Total Amount:</strong>
                  </td>
                  <td className="text-end">
                    <strong style={{ fontSize: "1.1rem" }}>
                      ₹{totalAmount.toFixed(2)}
                    </strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Footer Actions */}
      <div className="d-flex gap-2">
        <button onClick={() => navigate("/ta-list")} className="btn btn-secondary">
          <i className="bi bi-arrow-left me-2"></i>Back to List
        </button>
      </div>
    </Layout>
  );
};

export default TAView;
